import { useCallback, useMemo } from 'react';

import { Point3D as Point3DInterface } from '@paragon/contract-library';

import { useAppSelector } from '../../components/hooks';
import { convertFromDesignToLayoutCoordinates } from '../../library/layout-geometry/coordinates';
import { inferInsertionWorkLine, InsertionWorkLine, WorkLineSnap } from '../../library/layout-geometry/work-lines';
import { usePointPickingPointSnapper } from '../../library/layout-snapping/point-snapping/snapMousePosition';
import { getWorkLineInsertionInfo } from '../../store/selectors/workLineInsertionSelectors';
import { useMousePositionInScene } from '../hooks/useMousePositionInScene';
import { useStandardLineSnapper } from './LinePickingVisualsAndEventListeners';

interface WorkLineInsertionInteraction {
  readonly liveSnap: WorkLineSnap | null;
  readonly liveLine: InsertionWorkLine | null;

  readonly snapAtWorldPosition: (worldPos: Point3DInterface) => WorkLineSnap;
}

export function useWorkLineInsertionSnapper(): WorkLineInsertionInteraction {
  const insertionInfo = useAppSelector(getWorkLineInsertionInfo);
  const snappingLevel = useAppSelector((state) => state.application.snappingLevel);
  const inserting = insertionInfo != null;
  const active = inserting && insertionInfo.draft.phase !== 'reviewing';

  const pointSnapper = usePointPickingPointSnapper({ enabled: inserting });
  const snapToLine = useStandardLineSnapper({ enabled: inserting });
  const mousePosition = useMousePositionInScene(active);

  const snapAtWorldPosition = useCallback(
    (worldPos: Point3DInterface): WorkLineSnap => {
      const cursorPoint = convertFromDesignToLayoutCoordinates(worldPos).projectTo2D();
      return {
        unsnappedPoint: cursorPoint,
        pointSnap: pointSnapper(cursorPoint),
        lineSnap: snapToLine(worldPos),
      };
    },
    [pointSnapper, snapToLine],
  );

  const liveSnap = useMemo(
    (): WorkLineSnap | null => (active && mousePosition != null ? snapAtWorldPosition(mousePosition) : null),
    [active, mousePosition, snapAtWorldPosition],
  );

  const liveLine = useMemo((): InsertionWorkLine | null => {
    const draft = insertionInfo?.draft;
    const firstSelection = draft?.phase === 'gesturing' ? draft.firstSelection : null;
    const settings = insertionInfo?.settings;
    if (firstSelection == null || settings == null || liveSnap == null) return null;

    return inferInsertionWorkLine(firstSelection, liveSnap, settings, snappingLevel);
  }, [insertionInfo, snappingLevel, liveSnap]);

  return { liveSnap, liveLine, snapAtWorldPosition };
}
